/*  MUI  */
import { Box } from '@auviknetworks/iceberg-ui';
import { Grid, Typography } from '@mui/material';

/*  Shared  */
import { BreakdownCard } from 'application/Shared/Components/Cards/Components/BreakdownCard';
import { DataVisualCard } from 'application/Shared/Components/DataAPIVisualsCard';
import { useManagedTableV2Context } from 'application/Shared/Components/Table/ManagedTableV2ContextProvider';
import { FontSizes } from 'application/Shared/Constants';
import { TenantLicenseUsage } from 'application/Shared/types/tenantLicenseUsage';

/*  React  */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    LicenseCardData,
    LicenseTypesFlyoutMenu
} from './LicenseTypesFlyoutMenu';

const MAX_LICENSE_TYPES = 5;

type DataType = 'undetected' | 'underutilized' | 'total';

type FlyoutState = {
    title: string;
    dataType: DataType | null;
};

// Configuration for filter application
const FILTER_CONFIGS: Record<
    DataType,
    (licenseType: string) => { id: string; value: string[] }[]
> = {
    total: (licenseType) => [{ id: 'licenseType', value: [licenseType] }],
    underutilized: (licenseType) => [
        { id: 'licenseType', value: [licenseType] },
        { id: 'status', value: ['underutilized'] }
    ],
    undetected: (licenseType) => [
        { id: 'licenseType', value: [licenseType] },
        { id: 'status', value: ['undetected'] }
    ]
};

/**
 * Calculates the total number of licenses in the data set.
 * @param data - The data to calculate the total number of licenses from.
 * @returns The total number of licenses.
 */
function getTotalLicenseCount(data: LicenseCardData[]) {
    return data.reduce((acc, curr) => acc + curr.value, 0);
}

export const IntegrationAppUserDataCards = () => {
    const { t } = useTranslation();
    const [licenseTypes, setLicenseTypes] = useState<string[]>([]);
    const [currentFilter, setCurrentFilter] = useState<DataType | null>(null);
    const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
    const [flyoutState, setFlyoutState] = useState<FlyoutState>({
        dataType: null,
        title: ''
    });

    const { data: rowData, table } =
        useManagedTableV2Context<TenantLicenseUsage>();

    const data = table.getFilteredRowModel().rows.map((d) => d.original);
    const { columnFilters } = table.getState();

    // Store an updated list of all the different license types
    useEffect(() => {
        if (rowData) {
            const licenseTypeSet = new Set(
                rowData.map((d) => d['licenseTier+skuName'])
            );
            setLicenseTypes(Array.from(licenseTypeSet) as string[]);
        }
    }, [rowData]);

    const getFilterById = useCallback(
        (id: string) => {
            const columnFilter = columnFilters?.find(
                (filter) => filter.id === id
            );
            return {
                id,
                value: (columnFilter?.value ?? []) as string[]
            };
        },
        [columnFilters]
    );

    const updateFilters = useCallback(
        (licenseType: string, dataType: DataType) => {
            const licenseFilter = getFilterById('licenseType');
            const isAlreadyFiltered =
                currentFilter === dataType &&
                licenseFilter.value.includes(licenseType);

            if (isAlreadyFiltered) {
                table.resetColumnFilters();
                setCurrentFilter(null);
            } else {
                const filters = FILTER_CONFIGS[dataType](licenseType);
                table.setColumnFilters(filters);
                setCurrentFilter(dataType);
            }
        },
        [currentFilter, getFilterById, table]
    );

    const createLicenseCardData = useCallback(
        (
            dataSource: TenantLicenseUsage[] | undefined
        ): {
            total: LicenseCardData[];
            underutilized: LicenseCardData[];
            undetected: LicenseCardData[];
        } => {
            const emptyResult = {
                total: [] as LicenseCardData[],
                underutilized: [] as LicenseCardData[],
                undetected: [] as LicenseCardData[]
            };

            if (!dataSource?.length || !licenseTypes.length) return emptyResult;

            // Count licenses by type and status
            const licenseCounts = dataSource.reduce<
                Record<DataType, Record<string, number>>
            >(
                (acc, license) => {
                    const licenseKey =
                        license['licenseTier+skuName'] || t('Common.none');

                    // Count total
                    acc.total[licenseKey] = (acc.total[licenseKey] || 0) + 1;

                    // Count by status
                    if (license.status.includes('undetected')) {
                        acc.undetected[licenseKey] =
                            (acc.undetected[licenseKey] || 0) + 1;
                    }
                    if (license.status.includes('underutilized')) {
                        acc.underutilized[licenseKey] =
                            (acc.underutilized[licenseKey] || 0) + 1;
                    }

                    return acc;
                },
                { total: {}, underutilized: {}, undetected: {} }
            );

            // Transform counts into card data
            const cardData = {
                total: [] as LicenseCardData[],
                underutilized: [] as LicenseCardData[],
                undetected: [] as LicenseCardData[]
            };

            const dataTypes: DataType[] = [
                'total',
                'underutilized',
                'undetected'
            ];

            licenseTypes.forEach((licenseType) => {
                const label = licenseType || t('Common.none');

                dataTypes.forEach((dataType) => {
                    cardData[dataType].push({
                        id: `${dataType}_${licenseType.toLowerCase()}`,
                        label,
                        onClick: () => updateFilters(licenseType, dataType),
                        value: licenseCounts[dataType][licenseType] || 0
                    });
                });
            });

            // Sort all arrays by value (desc) then label (asc)
            dataTypes.forEach((dataType) => {
                cardData[dataType].sort(
                    (a, b) =>
                        b.value - a.value || a.label.localeCompare(b.label)
                );
            });

            return cardData;
        },
        [licenseTypes, t, updateFilters]
    );

    // Compute unfiltered data once - only recalculates when rowData changes
    const unfilteredData = useMemo(
        () => createLicenseCardData(rowData),
        [createLicenseCardData, rowData]
    );

    // Compute filtered data - only recalculates when filtered data changes
    const filteredData = useMemo(
        () => createLicenseCardData(data),
        [createLicenseCardData, data]
    );

    // Derive the appropriate data to display based on applied filter
    const allLicencesData =
        currentFilter === 'total' ? filteredData.total : unfilteredData.total;

    const undetectedLicensesData =
        currentFilter === 'undetected'
            ? filteredData.undetected
            : unfilteredData.undetected;

    const underUtilizedLicensesData =
        currentFilter === 'underutilized'
            ? filteredData.underutilized
            : unfilteredData.underutilized;

    const isLastLoginTimeAvailable = useMemo(
        () => rowData?.some((d) => !!d.metadata?.lastLoginTime),
        [rowData]
    );

    // Generate flyout data based on current state
    const dynamicFlyoutData = useMemo(() => {
        if (!flyoutState.dataType) return [];

        return unfilteredData[flyoutState.dataType];
    }, [flyoutState.dataType, unfilteredData]);

    const showFlyout = useCallback(
        ({ dataType, title }: { dataType: DataType; title: string }) => {
            setIsFlyoutOpen(true);
            setFlyoutState({ dataType, title });
        },
        []
    );

    return (
        <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={4}>
                <BreakdownCard
                    data={allLicencesData}
                    filteredResults={currentFilter === 'total'}
                    maxTypes={MAX_LICENSE_TYPES}
                    onClickSeeAll={() =>
                        showFlyout({
                            dataType: 'total',
                            title: t('SaasOps.licenses.total', {
                                count: getTotalLicenseCount(allLicencesData)
                            })
                        })
                    }
                    seeAllLabel={t('LicenseData.card.seeAll')}
                    title={t('SaasOps.licenses.total', {
                        count: getTotalLicenseCount(allLicencesData)
                    })}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                <BreakdownCard
                    data={undetectedLicensesData}
                    filteredResults={currentFilter === 'undetected'}
                    maxTypes={MAX_LICENSE_TYPES}
                    onClickSeeAll={() =>
                        showFlyout({
                            dataType: 'undetected',
                            title: t('SaasOps.licenses.total.undetected', {
                                count: getTotalLicenseCount(
                                    undetectedLicensesData
                                )
                            })
                        })
                    }
                    seeAllLabel={t('LicenseData.card.seeAll')}
                    title={t('SaasOps.licenses.total.undetected', {
                        count: getTotalLicenseCount(undetectedLicensesData)
                    })}
                    tooltip={t('License.UndetectedAccounts')}
                />
            </Grid>
            <Grid item xs={12} md={4}>
                {isLastLoginTimeAvailable ? (
                    <BreakdownCard
                        data={underUtilizedLicensesData}
                        filteredResults={currentFilter === 'underutilized'}
                        maxTypes={MAX_LICENSE_TYPES}
                        onClickSeeAll={() =>
                            showFlyout({
                                dataType: 'underutilized',
                                title: t(
                                    'SaasOps.licenses.total.underutilized',
                                    {
                                        count: getTotalLicenseCount(
                                            underUtilizedLicensesData
                                        )
                                    }
                                )
                            })
                        }
                        seeAllLabel={t('LicenseData.card.seeAll')}
                        title={t('SaasOps.licenses.total.underutilized', {
                            count: getTotalLicenseCount(
                                underUtilizedLicensesData
                            )
                        })}
                        tooltip={t(
                            'License.TotalNumberOfUnderutilizedLicenses'
                        )}
                    />
                ) : (
                    <DataVisualCard
                        title={t('SaasOps.licenses.total.underutilized', {
                            count: null as unknown as number
                        })}
                    >
                        <Box
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height="100%"
                            width="100%"
                        >
                            <Typography sx={{ fontSize: FontSizes[2] }}>
                                {t('Common.n/a')}
                            </Typography>
                        </Box>
                    </DataVisualCard>
                )}
            </Grid>
            {dynamicFlyoutData.length > MAX_LICENSE_TYPES && isFlyoutOpen && (
                <LicenseTypesFlyoutMenu
                    data={dynamicFlyoutData}
                    onClose={() => setIsFlyoutOpen(false)}
                    title={flyoutState.title}
                />
            )}
        </Grid>
    );
};
